import api from './api';
import { buildQueryParams } from './utils/query-params';
import type {
  ApiResponse,
  PaginatedResponse,
  Lead,
  LeadStatus,
  LeadSource,
  LeadStats,
  LeadFilters,
  CreateLeadData,
  UpdateLeadData,
  BulkLeadAction,
  BulkLeadActionResult,
  LeadNote,
  AssignableUser,
  AssignableTeam,
  GoogleFormOption,
  LandingFormOption,
  AdFieldOption,
  MetaFormOption,
} from '@/types';

// Lead Statuses
export const getLeadStatuses = async (activeOnly = false) => {
  const query = buildQueryParams({ active_only: activeOnly || undefined });
  const response = await api.get<ApiResponse<LeadStatus[]>>(`/lead-statuses?${query}`);
  return response.data.data;
};

// Lead Sources
export const getLeadSources = async (activeOnly = false) => {
  const query = buildQueryParams({ active_only: activeOnly || undefined });
  const response = await api.get<ApiResponse<LeadSource[]>>(`/lead-sources?${query}`);
  return response.data.data;
};

// Leads
export const getLeads = async (filters: LeadFilters = {}) => {
  const query = buildQueryParams(filters);
  const response = await api.get<PaginatedResponse<Lead>>(`/leads?${query}`);
  return response.data;
};

/**
 * Per-role dashboard — the "needs an owner" queue: leads with no
 * `assigned_to`, newest first. Thin wrapper over {@link getLeads} using the
 * index endpoint's boolean `unassigned` filter; returns the full paginated
 * envelope so the widget can show `meta.total` ("N unassigned") beside the
 * first `limit` rows.
 *
 * Gate the call on `leads.view.all` — a user scoped to `leads.view.assigned`
 * can by definition never see an unassigned lead, so the widget should be
 * omitted rather than rendered empty.
 */
export const getUnassignedLeads = async (limit = 5) => {
  return getLeads({
    unassigned: true,
    sort: 'created_at',
    direction: 'desc',
    page: 1,
    per_page: limit,
  });
};

// Phase 45 (D-10, RBAC-14): picker-only reference shape — 4 fields.
// MUST match backend LeadReferenceResource: id, first_name, last_name, phone.
// Never extend with email/status/assigned_to — those are visibility leaks (Pitfall 4).
export type LeadReference = {
  id: string;
  first_name: string;
  last_name: string;
  phone: string;
};

// Phase 45 (D-10, RBAC-14): picker endpoint. Gated server-side by
// `permission:leads.reference` + `throttle:60,1`. Use this (NOT getLeads)
// for Appointment/Quote/Invoice lead-selector typeaheads — bypasses
// ScopeByUserVisibility so a front-desk operator with `leads.reference`
// but NOT `leads.view.*` can still pick any lead.
export const getLeadsForPicker = async (
  params: {
    q?: string;
    per_page?: number;
    context?: "appointment";
    /** Converted patients only — for records that need a client (lab orders). */
    clients_only?: boolean;
  } = {},
) => {
  const query = buildQueryParams({
    q: params.q,
    per_page: params.per_page,
    context: params.context,
    clients_only: params.clients_only ? 1 : undefined,
  });
  const response = await api.get<PaginatedResponse<LeadReference>>(
    `/leads/search${query ? `?${query}` : ''}`,
  );
  return response.data;
};

export type LeadLookup = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  /** null when the caller lacks `leads.view.phone`. */
  phone: string | null;
  chart_number: number | null;
  /** The list that holds the person — /leads before conversion, /clients after
   *  (always `leads` on unified-patients clinics). */
  list: 'leads' | 'clients';
};

// Dentour 261019 — the ⌘K palette's record search (max 8 hits). Unlike the
// picker above it keeps ScopeByUserVisibility: it opens records, so it only
// finds what the caller's own lists hold. 403 without a leads.view.* grant.
export const lookupLeads = async (q: string) => {
  const response = await api.get<{ data: LeadLookup[] }>(
    `/leads/lookup?${buildQueryParams({ q })}`,
  );
  return response.data;
};

export type LeadNavigation = {
  prev_id: string | null;
  next_id: string | null;
};

// `listQuery` is the leads list's active filter+sort query string (no leading
// `?`), forwarded so the backend computes prev/next within the same filtered,
// ordered set the user was viewing. Omit it for direct/refresh loads, where
// navigation falls back to the default created_at order.
export const getLead = async (
  id: string,
  listQuery?: string,
): Promise<{ lead: Lead; navigation: LeadNavigation }> => {
  const suffix = listQuery ? `?${listQuery}` : '';
  const response = await api.get<ApiResponse<Lead> & { navigation: LeadNavigation }>(
    `/leads/${id}${suffix}`,
  );
  return {
    lead: response.data.data,
    navigation: response.data.navigation ?? { prev_id: null, next_id: null },
  };
};

export const createLead = async (data: CreateLeadData) => {
  const response = await api.post<ApiResponse<Lead>>('/leads', data);
  return response.data.data;
};

// Flag-mode-aware create (features.duplicate_leads_flag_mode): when the lead
// was created as a flagged duplicate, the response carries a top-level
// duplicate_of describing the original lead so the form can surface it.
export const createLeadWithDuplicateMeta = async (data: CreateLeadData) => {
  const response = await api.post<ApiResponse<Lead> & { duplicate_of?: DuplicateLeadInfo }>('/leads', data);
  return { lead: response.data.data, duplicateOf: response.data.duplicate_of ?? null };
};

export const updateLead = async (id: string, data: UpdateLeadData) => {
  const response = await api.put<ApiResponse<Lead>>(`/leads/${id}`, data);
  return response.data.data;
};

export const deleteLead = async (id: string) => {
  await api.delete(`/leads/${id}`);
};

export const restoreLead = async (id: string) => {
  const response = await api.post<ApiResponse<Lead>>(`/leads/${id}/restore`);
  return response.data.data;
};

export const bulkLeadAction = async (data: BulkLeadAction) => {
  // The bulk endpoint answers with a flat body ({ message, affected_count, ... })
  // rather than the usual { data: … } envelope, so unwrap defensively.
  const response = await api.post<ApiResponse<BulkLeadActionResult> & Partial<BulkLeadActionResult>>(
    '/leads/bulk',
    data,
  );
  return (response.data.data ?? (response.data as unknown as BulkLeadActionResult)) as BulkLeadActionResult;
};

export const getLeadStats = async (filters: LeadFilters = {}) => {
  const query = buildQueryParams(filters);
  const response = await api.get<LeadStats>(`/leads/stats${query ? `?${query}` : ''}`);
  return response.data;
};

// Assignable Users
export const getAssignableUsers = async () => {
  const response = await api.get<{ data: AssignableUser[] }>('/leads/assignable-users');
  return response.data.data;
};

// Country Options (distinct country codes present on leads)
export const getLeadCountryOptions = async () => {
  const response = await api.get<{ data: string[] }>('/leads/country-options');
  return response.data.data;
};

// Meta Form Options (distinct Meta lead-gen forms present on submissions)
export const getLeadMetaFormOptions = async () => {
  const response = await api.get<{ data: MetaFormOption[] }>('/leads/meta-form-options');
  return response.data.data;
};

// Google Form Options (distinct Google Ads lead forms present on submissions).
// 404s when google_leads_enabled is off.
export const getLeadGoogleFormOptions = async () => {
  const response = await api.get<{ data: GoogleFormOption[] }>('/leads/google-form-options');
  return response.data.data;
};

// Landing Form Options (distinct landing-page forms present on submissions).
// 404s when landing_leads_enabled is off.
export const getLeadLandingFormOptions = async () => {
  const response = await api.get<{ data: LandingFormOption[] }>('/leads/landing-form-options');
  return response.data.data;
};

// Ad campaign / ad name options (Meta submissions + monday imports), each with
// its lead count. 404 when lead_ad_filters_enabled is off.
export const getLeadAdCampaignOptions = async () => {
  const response = await api.get<{ data: AdFieldOption[] }>('/leads/ad-campaign-options');
  return response.data.data;
};

export const getLeadAdNameOptions = async () => {
  const response = await api.get<{ data: AdFieldOption[] }>('/leads/ad-name-options');
  return response.data.data;
};

// Assign Lead
// ArtDent 260918 — the partners the desk may pick from (auth-only directory,
// like assignable-users: desk roles do not hold users.view).
export const getAffiliators = async () => {
  const response = await api.get<{ data: AssignableUser[] }>('/leads/affiliators');
  return response.data.data;
};

// Dentx 261030 — the last protocol number given (chart_number_protocol_enabled).
export const getLastChartNumber = async () => {
  const response = await api.get<{ data: { last: number | null } }>('/leads/chart-numbers/last');
  return response.data.data.last;
};

export const assignLead = async (leadId: string, assignedTo: number | null) => {
  const response = await api.post<ApiResponse<Lead>>(`/leads/${leadId}/assign`, { assigned_to: assignedTo });
  return response.data.data;
};

// Teams v2.1 — teams the current user may stamp on a lead. Separate from
// getTeams() (lib/teams-api.ts), which sits behind `teams.manage` and so 403s
// for the team leads this picker is meant to serve. 404s when teams are off.
export const getAssignableTeams = async () => {
  const response = await api.get<{ data: AssignableTeam[] }>('/leads/assignable-teams');
  return response.data.data;
};

// Route a lead to a team, independently of its assignee. `null` clears it.
export const assignLeadTeam = async (leadId: string, teamId: number | null) => {
  const response = await api.post<ApiResponse<Lead>>(`/leads/${leadId}/assign-team`, { team_id: teamId });
  return response.data.data;
};

// Convert Lead → Client (Phase 59 LCC-03)
// POST /leads/{id}/convert — idempotent on the backend (409 if already a client,
// DB UNIQUE on clients.lead_id is the real backstop). The page calls loadLead()
// after a 2xx to refresh the canonical converted state (badge + tab unlock), so
// the response payload is not consumed here.
export const convertLead = async (leadId: string) => {
  const response = await api.post<ApiResponse<Lead>>(`/leads/${leadId}/convert`);
  return response.data.data;
};

// Undo Lead → Client conversion.
// DELETE /leads/{id}/convert — force-deletes the satellite client so the lead
// reads as a plain lead again. 409 if the lead was never converted; 422 when
// the client already owns clinical records (tooth chart / lab orders /
// treatments) — surface that message to the user.
export const unconvertLead = async (leadId: string) => {
  const response = await api.delete<ApiResponse<Lead>>(`/leads/${leadId}/convert`);
  return response.data.data;
};

// Recycle Lead (multi-brand lead recycling)
// POST /leads/{id}/recycle — starts a new round on the other brand (or {brand_id}
// if supplied). Returns the updated lead so callers can refresh the brand pill +
// attempt history.
export const recycleLead = async (leadId: string, brandId?: string) => {
  const response = await api.post<ApiResponse<Lead>>(
    `/leads/${leadId}/recycle`,
    brandId ? { brand_id: brandId } : {},
  );
  return response.data.data;
};

// POST /leads/{id}/recycle-undo — reverts the most recent recycle round.
export const undoRecycleLead = async (leadId: string) => {
  const response = await api.post<ApiResponse<Lead>>(`/leads/${leadId}/recycle-undo`);
  return response.data.data;
};

// Lead Notes
export const getLeadNotes = async (leadId: string) => {
  const response = await api.get<ApiResponse<LeadNote[]>>(`/leads/${leadId}/notes`);
  return response.data.data;
};

// `for_front_desk` (Etrusk 260813) marks a doctor's note to the desk and bells
// every active coordinator; it is the only note a kartele-only user may create.
export const createLeadNote = async (
  leadId: string,
  data: { content: string; is_pinned?: boolean; for_front_desk?: boolean; on_work_sheet?: boolean },
) => {
  const response = await api.post<ApiResponse<LeadNote>>(`/leads/${leadId}/notes`, data);
  return response.data.data;
};

export const updateLeadNote = async (leadId: string, noteId: string, data: { content?: string; is_pinned?: boolean }) => {
  const response = await api.put<ApiResponse<LeadNote>>(`/leads/${leadId}/notes/${noteId}`, data);
  return response.data.data;
};

export const deleteLeadNote = async (leadId: string, noteId: string) => {
  await api.delete(`/leads/${leadId}/notes/${noteId}`);
};

export const toggleLeadNotePin = async (leadId: string, noteId: string) => {
  const response = await api.post<ApiResponse<LeadNote>>(`/leads/${leadId}/notes/${noteId}/toggle-pin`);
  return response.data.data;
};

// ArtDent 261007 — put a note on the treatment work sheet, or take it off.
export const toggleLeadNoteWorkSheet = async (leadId: string, noteId: string) => {
  const response = await api.post<ApiResponse<LeadNote>>(`/leads/${leadId}/notes/${noteId}/toggle-work-sheet`);
  return response.data.data;
};


// Pre-submit duplicate warning (ArtDent 260924): the same match the guard
// would 409 on, or null when nothing collides or the clinic does not run
// prevent_duplicate_leads. `canOverride` says whether THIS user may send
// allow_duplicate afterwards, so the form knows whether to offer "Create anyway".
export const checkDuplicatePhone = async (params: {
  phone: string;
  phone_secondary?: string;
  country_code?: string;
  ignore?: string;
}) => {
  const query = new URLSearchParams();
  query.set("phone", params.phone);
  if (params.phone_secondary) query.set("phone_secondary", params.phone_secondary);
  if (params.country_code) query.set("country_code", params.country_code);
  if (params.ignore) query.set("ignore", params.ignore);
  const response = await api.get<ApiResponse<DuplicateLeadInfo | null> & { can_override?: boolean }>(
    `/leads/duplicate-check?${query.toString()}`
  );
  return { duplicate: response.data.data ?? null, canOverride: response.data.can_override ?? false };
};

// Phone Lookup
export const lookupLeadByPhone = async (phone: string) => {
  const response = await api.get<ApiResponse<{
    id: string;
    first_name: string;
    last_name: string;
    full_name: string;
    phone: string;
    status: string | null;
    assigned_to: { id: number; name: string } | null;
    last_call: {
      id: string;
      direction: string;
      started_at: string;
      duration_seconds: number | null;
    } | null;
  } | null>>(`/leads/lookup-by-phone?phone=${encodeURIComponent(phone)}`);
  return response.data.data;
};

// Import/Export

export interface ImportResult {
  message: string;
  success_count: number;
  skipped_count: number;
  errors: Array<{
    row: number;
    errors: string[];
    data: {
      first_name: string;
      last_name: string;
      phone: string;
    };
  }>;
  duplicate_count?: number;
  duplicates?: Array<{
    row: number;
    phone: string;
    reason: 'existing' | 'in_file';
    existing_lead_id: string | null;
    existing_lead_name: string | null;
    data: {
      first_name: string;
      last_name: string;
      phone: string;
    };
  }>;
}

// 409 payload returned by POST/PUT /leads when features.prevent_duplicate_leads
// blocks a phone collision ({ message, error: 'duplicate_lead', duplicate }).
export interface DuplicateLeadInfo {
  id: string;
  name: string;
  phone: string | null;
  status: string | null;
  assigned_to: string | null;
  is_converted: boolean;
  created_at: string | null;
}

export interface LeadExportOptions {
  includeClinicStatus?: boolean;
  includeMetaForm?: boolean;
  includeNotes?: boolean;
}

export const exportLeadsCSV = async (filters: LeadFilters = {}, options: LeadExportOptions = {}) => {
  const query = buildQueryParams({
    ...filters,
    ...(options.includeClinicStatus ? { include_clinic_status: 1 } : {}),
    ...(options.includeMetaForm ? { include_meta_form: 1 } : {}),
    ...(options.includeNotes ? { include_notes: 1 } : {}),
  });
  const response = await api.get(`/leads/export?${query}`, {
    responseType: 'blob',
  });

  // Create download link
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `leads-${new Date().toISOString().split('T')[0]}.csv`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const downloadImportTemplate = async () => {
  const response = await api.get('/leads/export/template', {
    responseType: 'blob',
  });

  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', 'leads-import-template.csv');
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const importLeads = async (file: File): Promise<ImportResult> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post<ImportResult>('/leads/import', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};

/**
 * Radiance 261007 — tick or untick one of the clinic coordinator's intake
 * steps on a patient: the consent or the preliminary measure. The
 * analysis is never ticked — filing it in the Analyses folder is
 * what marks it done.
 */
export const setLeadIntakeStep = async (
  leadId: string,
  step: 'consent' | 'measure',
  done: boolean,
): Promise<void> => {
  await api.patch(`/leads/${leadId}/intake`, { step, done });
};
